/**
 * Auth-specific API calls.
 *
 * `login` bypasses the shared `request()` helper because /v2/auth/token
 * uses form-urlencoded (oauth2 password grant) and runs BEFORE the auth
 * header is available.
 *
 * `fetchCurrentUser` goes through `getJsonReal` so it shares the
 * in-flight dedup with the rest of the app — strict-mode double-render
 * during bootstrap won't produce two `/v2/user/me/` requests.
 *
 * NOTE: auth/user/states moved from v1 to v2 (jun-16 backend cleanup;
 * v1 was deleted). `requestPasswordReset` still points at the v1 mock
 * path below — it's intercepted by the mock layer and doesn't hit the
 * network, so it's left until the reset flow is wired for real.
 *
 * `requestPasswordReset` goes through the shared wrapper so the mock
 * layer can intercept it — backend hasn't shipped this endpoint yet.
 *
 * Endpoints mirror orion-frontend so the same backend serves both UIs.
 */
import { getJsonReal, request } from "../api/client";
import type { TokenPayload, User } from "./types";

// Strip trailing slash so we don't end up with `//v1/...` when the env
// var follows orion-frontend's convention of including the slash.
const API_BASE_URL = (import.meta.env.VITE_ORION_API ?? "").replace(/\/$/, "");

class AuthError extends Error {
    constructor(
        public status: number,
        message: string,
    ) {
        super(message);
        this.name = "AuthError";
    }
}

/** OAuth2 password grant. Returns the access token; caller decides
 *  where to persist it. */
export async function login(email: string, password: string): Promise<TokenPayload> {
    const body = new URLSearchParams({ username: email.trim(), password });

    const res = await fetch(`${API_BASE_URL}/v2/auth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
    });

    if (!res.ok) {
        const detail = await readDetail(res);
        throw new AuthError(res.status, detail || "Email or password is incorrect");
    }

    const json = (await res.json()) as { access_token?: string };
    if (!json.access_token) {
        throw new AuthError(500, "Server did not return an access token");
    }
    return { accessToken: json.access_token };
}

/** Fetch the current user. The 401 path is what the AuthProvider uses
 *  to decide whether the persisted token is still good on reload.
 *
 *  Uses `getJsonReal` so the in-flight dedupe in client.ts also covers
 *  this call — under React strict mode the bootstrap useEffect runs
 *  twice, but only one network request goes out. The Authorization
 *  header is sourced from `setRequestToken` (which auth-context sets
 *  immediately before this fires), so we don't need to pass the token
 *  in. */
export async function fetchCurrentUser(): Promise<User> {
    return getJsonReal<User>("/v2/user/me/");
}

async function readDetail(res: Response): Promise<string> {
    try {
        const json = (await res.json()) as { detail?: string };
        return json.detail ?? "";
    } catch {
        return "";
    }
}

/** Kick off the password-reset flow.
 *
 *  Goes through the shared `request()` wrapper so the mock layer can
 *  intercept it. When the backend ships /v1/auth/forgot-password the
 *  mock handler in `mock-handlers.ts` becomes obsolete and the same
 *  call lights up against the real API automatically. */
export async function requestPasswordReset(email: string): Promise<void> {
    const res = await request("/v1/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
    });
    if (!res.ok) {
        const detail = await readDetail(res);
        throw new AuthError(res.status, detail || "Could not send the reset link. Try again.");
    }
}

export { AuthError };
